<template>
  <div class="flex flex-row gap-4 flex-nowrap">
    <div class="flex justify-center h-24 w-24 flex-shrink-0 border">
      <UnLazyImage
        :src="logo"
        class="max-h-full max-w-full object-contain"
        @error="setDefaultImage"
      />
    </div>
    <div class="flex flex-col grow gap-1">
      <p class="text-md font-bold line-clamp-1 break-all">{{ data.name }}</p>
      <p class="text-sm line-clamp-1 break-all text-gray-500">
        {{ data.sub_categories.join(', ') }}
      </p>
      <p class="text-sm line-clamp-1">{{ tags.join(' | ') }}</p>
      <div class="flex">
        <p class="text-xs text-orange-500 font-bold cursor-pointer" @click="viewProviderInfo">
          View info
        </p>
        <h6
          v-if="isLocationReady && locationList.length > 1"
          class="text-xs text-orange-500 font-bold cursor-pointer mx-3"
          @click="openLocationModal"
        >
          Change Location
        </h6>
        <h6 v-else-if="!isLocationReady" class="text-xs text-gray-400 font-medium mx-3">
          Loading...
        </h6>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, onMounted,watch} from 'vue';
import { V3ModalComponent, V3PopupComponent } from '#components';
import { popupBackHandler } from '@/composables/popupBackHandler';



const { isMobile } = useDevice();
const { gps } = useLocation();
const runtimeConfig = useRuntimeConfig();

const props = defineProps({
  data: { type: Object, required: true },
  fandb: { type: Boolean, default: true }
});

// --- Integrate popupBackHandler ---
const { isPopupOpen, openPopup, closePopup } = popupBackHandler();


// Image handling
const logo = useSecureImage(
  props.data?.images?.thumbnail || props.data?.images?.gallery[0]
);
const setDefaultImage = (event) => {
  event.imageSrc = runtimeConfig.public.defaultImage;
};

// Tags
const timeToShip = useCalculateTAT(ref(props.data), ref(props.data.categories[0]));
const tags = ref([
  ...(props.fandb && timeToShip.value ? [timeToShip.value] : []),
  useDistanceText(props.data.distance),
  props.fandb
    ? props.data.locations.address.locality
    : props.data.locations.address.city
]);

// Modal logic
const slideover = useSlideover();
const modal = useModal();

// const openModal = (component, payload) => {
//   // Ensure the popup is marked as open in history
//   openPopup();
//   const options = {
//     component,
//     data: payload,
//     //onClose: isMobile ? slideover.close : modal.close
//     onClose: () => {
//       isMobile.value ? slideover.close() : modal.close();
//       //closePopup(); // This tells the history handler to go back
//     }
//   };
//   isMobile ? slideover.open(V3PopupComponent, options) : modal.open(V3ModalComponent, options);
// };

const openModal = (component, payload) => {
  // openPopup(); // Mark the popup as open in history
  const options = {
    component,
    data: payload,
    onClose: () => {
      // Close the popup when the "X" button is clicked
      if (isMobile.value) {
        slideover.close();
      } else {
        modal.close();
      }
      history.replaceState(null, '', window.location.href);
    },
  };
  if (isMobile.value) {
    slideover.open(V3PopupComponent, options);
  } else {
    modal.open(V3ModalComponent, options);
  }
};


// watchEffect(() => {
//   if (isPopupOpen.value && !modal.isOpen.value && !slideover.isOpen.value) {
//     closePopup(); // ensures we trigger history.back() even if closed by outside click
//   }
// });

const viewProviderInfo = () => openModal('provider', props.data);
const openLocationModal = () => openModal('providerLocation2', locationList.value);

// Watch isPopupOpen to close UI components when back button is pressed ---
// watch(isPopupOpen, (newVal) => {
//   if (!newVal) {
//     if (slideover.isOpen.value) slideover.close();
//     if (modal.isOpen.value) modal.close();
//   }
// });


// Location data
const locationList = ref([]);
const isLocationReady = ref(false);

onMounted(() => fetchProviderLocations());

async function fetchProviderLocations() {
  const { ukid, categories } = props.data;
  const radius = categories[0].toLowerCase() === 'f&b' ? 10000 : 50000;

  try {
    const { data } = await useAPIHandler('get', '/providers/locations', {
      query: { ukid, radius }
    });

    if (data.message?.toLowerCase() === 'success') {
      locationList.value = await processLocations(data.data);
    }
  } catch (err) {
    console.error('Provider fetch error:', err.message);
  } finally {
    isLocationReady.value = true;
  }
}

async function processLocations(fetchedLocations) {
  const filtered = [];

  for (const provider of fetchedLocations || []) {
    // const timing = useTimingCheck(provider.availability, provider.locations);

    // Ensure locations is an array
    const locations = Array.isArray(provider.locations) ? provider.locations : [provider.locations];

    for (const loc of locations) {
      const isServiceable = await checkServiceability(provider.ukid, loc.id);
      if (!isServiceable) continue;

      const distanceText = formatDistance(loc.distance);
      const locality = [loc?.address?.locality, loc?.address?.city].filter(Boolean).join(', ');

      filtered.push({
        ukid: provider.ukid,
        id: loc.id,
        distance: distanceText,
        locality,
        isOpened: provider.is_open_now
      });
    }
  }

  return filtered;
}

async function checkServiceability(ukid, locationId) {
  try {
    const { data } = await useAPIHandler('GET', `/providers/${ukid}/service-check`, {
      query: {
        lat: gps.latitude,
        lng: gps.longitude,
        location_id: locationId,
        partner_id: useRuntimeConfig().public.partnerCode
      }
    });
    return data?.serviceability ?? false;
  } catch (err) {
    console.error("Serviceability check failed:", err);
    return false;
  }
}

function formatDistance(meters) {
  if (meters <= 1000) {
    return `${Math.ceil(meters / 10) * 10}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
}
</script>

